import { useState } from 'react';
import { unifiedLogin, UnifiedLoginArgs } from 'utils/unifiedLogin';

type UnifiedLoginArgsNeeded = Omit<UnifiedLoginArgs, 'newTab'>;

interface UseAltaLoginArgs extends UnifiedLoginArgsNeeded {
  setError: (error: string) => void;
}

/**
 * @description hooke that returns a handler already setup with various options to login to Aware
 * when we have a token and redirect to the urlToOpen.
 * @params
 *  url: endpoint to call setOpenpathCookie on
 *  urlToOpen: url to redirect to after logging in successfully
 *  options: org_id and region to be passed to the urlToOpen
 *  setError: method to call when an error happens
 *  nonce: a value used by Aware when setting our cookie in the new auth strategy
 * @returns {
 *  handleAltaLogin: handler to login to aware.
 *  isLoggingIn: flag to mark if we are currently logging in.
 * }
 */
export const useAltaLogin = ({
  url,
  urlToOpen,
  options,
  setError,
}: UseAltaLoginArgs) => {
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  /**
   * Potentially need to rethink this hook but for the sake of not breaking everything
   * lets do this later. It was assumed we would know about these values ahead of time thus
   * why the args were passed through but there is now at least 1 scenario via unified login
   * where if a customer is video only based on their tokenScopeList we want to use this.
   * Since this was only initially used for logging in when redirecting from Aware this made
   * sense but now people will start going to the unified login page by default
   */
  const handleAltaLogin = async (overrides?: Partial<UseAltaLoginArgs>) => {
    setIsLoggingIn(true);
    try {
      await unifiedLogin({
        url,
        urlToOpen,
        options,
        newTab: false,
        ...overrides,
      });
    } catch (err) {
      const typedError = err as Error;

      setError(typedError.message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  return {
    handleAltaLogin,
    isLoggingIn,
  };
};
